/* eslint-disable react/prop-types */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { includes } from 'lodash';
import { useContext, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ClearanceStatusEnum,
  ConnectedFacility,
  ConnectedFacilityService,
  ConnectionFacilityRelationshipStatusWrite,
  ConnectionRequestStatus,
  FacilityRejectionReasonEnum,
  FacilityStatusEnum,
  HealthcareEntityService,
  ProviderRejectionReasonEnum,
  UserTypeEnum,
} from '../../../api';
import { PresentationStatusContext } from '../../contexts/PresentationStatusContext';
import { SessionContext } from '../../contexts/SessionContext';
import { SIZE_LG, useBreakpoint } from '../../hooks/breakpoint';
import { connectionRequestsQueryKeys } from '../../query-keys-factories/connection-requests';
import { healthcareEntityFacilityQueryKeys } from '../../query-keys-factories/facilities';
import { handleApiError } from '../../utils/errorhandling';
import { Card } from '../Card';
import { ConfirmationModal } from '../ConfirmationModal';
import { ReviewConnectionDialog } from '../ConnectionRequestCard/ReviewConnectionDialog';
import { ConnectionCombinedStatusBadge } from '../ConnectionStatusBadge';
import { DenyPresentationDialog } from '../DenyPresentationDialog';
import { FacilityCard } from '../FacilityCard';
import { PresentationStatusControl } from '../PresentationStatusControl';
import { Table } from '../Table';

const LIST_SIZE = 3;

interface ConnectedFacilitiesListProps {
  providerId: string;
  isNotCleared: boolean;
  canChangePresentationStatus?: boolean;
}
/* eslint-disable sonarjs/cognitive-complexity*/
export const ConnectedFacilitiesList = ({
  providerId,
  isNotCleared,
  canChangePresentationStatus,
}: ConnectedFacilitiesListProps) => {
  const [denialId, setDenaliId] = useState<string | null>(null);
  const [approvalId, setApprovalId] = useState<string | null>(null);
  const [showFullList, setShowFullList] = useState(false);
  const breakpoint = useBreakpoint();
  const isGrid = useMemo(() => includes(breakpoint, SIZE_LG), [breakpoint]);
  const [effectiveListSize, setEffectiveListSize] = useState<number>(LIST_SIZE);
  const [selectedFacility, setSelectedFacility] = useState<ConnectedFacility | null>(null);
  const { user } = useContext(SessionContext);
  const userIsProvider = user?.user_type == UserTypeEnum.PROVIDER;

  const connectionQueryParams: Parameters<
    typeof ConnectedFacilityService.connectedFacilityList
  >[0] = {
    provider: [providerId],
    ordering: '-updated_at',
  };
  const { isLoading, data } = useQuery({
    queryKey: connectionRequestsQueryKeys.list(connectionQueryParams),
    queryFn: async () => {
      return ConnectedFacilityService.connectedFacilityList({
        provider: [providerId],
        ordering: '-updated_at',
      });
    },
    enabled: !!providerId,
    select: (data) => data.results,
  });

  const list = useMemo(() => {
    const fullList = (data as unknown as ConnectedFacility[]) || [];

    // Sort function to sequence items: Pending > Completed > Rejected > Canceled
    const sortedList = [...fullList].sort((a, b) => {
      const getStatusWeight = (status: string | undefined) => {
        if (
          status === ConnectionRequestStatus.PENDING_RESPONSE ||
          status === ConnectionRequestStatus.PENDING_SEND ||
          status === ClearanceStatusEnum.PENDING_CLEARANCE ||
          status === FacilityStatusEnum.PENDING_APPROVAL
        ) {
          return 1; // Top priority
        }
        if (status === FacilityStatusEnum.APPROVED) return 2;
        if (status === FacilityStatusEnum.DENIED) return 3;
        if (status === ConnectionRequestStatus.REJECTED) return 4;
        if (status === ConnectionRequestStatus.CANCELED) return 5;
        return 6; // Everything else
      };

      const weightA = getStatusWeight(a.combined_status);
      const weightB = getStatusWeight(b.combined_status);

      if (weightA !== weightB) {
        return weightA - weightB;
      }
      return 0;
    });

    // Count pending items
    const pendingCount = sortedList.filter(
      (item) =>
        item.combined_status === FacilityStatusEnum.PENDING_APPROVAL ||
        item.combined_status === ConnectionRequestStatus.PENDING_RESPONSE ||
        item.combined_status == ClearanceStatusEnum.PENDING_CLEARANCE
    ).length;

    // Use the larger of pendingCount or LIST_SIZE
    const effectiveListSize = Math.max(pendingCount, LIST_SIZE);
    setEffectiveListSize(effectiveListSize);

    if (showFullList) {
      return sortedList;
    }

    return sortedList.slice(0, effectiveListSize);
  }, [data, showFullList]);

  const queryClient = useQueryClient();

  const mutateMarkApproved = useMutation({
    mutationFn: async (connectedFacilityId: string) => {
      await HealthcareEntityService.healthcareEntityFacilitiesMarkApprovedCreate({
        connectedFacilityId,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(connectionRequestsQueryKeys.allLists());
    },
  });

  const mutateMarkPending = useMutation({
    mutationFn: async (connectedFacilityId: string) => {
      await HealthcareEntityService.healthcareEntityFacilitiesMarkPendingCreate({
        connectedFacilityId,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(connectionRequestsQueryKeys.allLists());
    },
  });

  const mutateMarkDenied = useMutation({
    mutationFn: async ({
      connectedFacilityId,
      data,
    }: {
      connectedFacilityId: string;
      data: ConnectionFacilityRelationshipStatusWrite;
    }) => {
      await HealthcareEntityService.healthcareEntityFacilitiesMarkRejectedCreate({
        connectedFacilityId,
        requestBody: data,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(connectionRequestsQueryKeys.allLists());
      await queryClient.invalidateQueries(healthcareEntityFacilityQueryKeys.allLists());
    },
  });

  const handleDenyPresentation = (result) => {
    if (result && denialId) {
      mutateMarkDenied.mutate({
        connectedFacilityId: denialId,
        data: {
          facility_rejection_reason: result.rejection_reason,
          facility_rejection_details: result.rejection_details,
        },
      });
    }

    setDenaliId(null);
  };

  const handleApprovePresentation = async () => {
    if (!approvalId) return;

    try {
      await mutateMarkApproved.mutateAsync(approvalId);
      setApprovalId(null);
      toast.success('Provider presentation approved');
    } catch (error) {
      handleApiError(error, {
        messagePrefix: 'Error approving presentation',
      });
    }
  };

  const onPresentationStatusChange = async ({
    status,
    id,
  }: {
    status: FacilityStatusEnum;
    id: string;
  }) => {
    switch (status) {
      case FacilityStatusEnum.APPROVED:
        setApprovalId(id);
        return;
      case FacilityStatusEnum.DENIED:
        setDenaliId(id);
        return;
      case FacilityStatusEnum.PENDING_APPROVAL:
        mutateMarkPending.mutate(id);
        return;
    }
  };

  const isDisabled = useMemo(
    () =>
      isNotCleared ||
      mutateMarkApproved.isLoading ||
      mutateMarkPending.isLoading ||
      mutateMarkDenied.isLoading ||
      !canChangePresentationStatus,
    [
      isNotCleared,
      mutateMarkApproved.isLoading,
      mutateMarkDenied.isLoading,
      mutateMarkPending.isLoading,
      canChangePresentationStatus,
    ]
  );

  const handleReviewDialogClose = () => {
    setSelectedFacility(null);
  };

  const columns = useMemo(
    () => [
      {
        header: 'Facility Name',
        accessorFn: (row) => ({
          name: row.facility.name,
          id: row.facility.id,
          opportunity: row.opportunity,
        }),
        cell: (props) => {
          const { name, id, opportunity } = props.getValue();
          return (
            <div className="d-flex align-items-center gap-2">
              <Link to={`/app/organization/facilities/${id}`}>{name}</Link>
              {opportunity && (
                <Link
                  to={`/app/organization/opportunities/${opportunity.id}`}
                  className="ms-1"
                  title="View Opportunity"
                >
                  <i className="fa-solid fa-arrow-up-right-from-square" />
                </Link>
              )}
            </div>
          );
        },
        size: 25,
        minSize: 0,
      },
      {
        header: 'Location',
        accessorFn: (data) => `${data.facility.city}, ${data.facility.state}`,
        cell: (props) => {
          const city_state = props.getValue();
          return <div className="small">{city_state}</div>;
        },
        size: 15,
        minSize: 0,
      },
      {
        header: 'Hourly Rates',
        accessorFn: (data) => ({
          day_rate: userIsProvider ? data.shift_rate_day : data.facility_rate_day,
          night_rate: userIsProvider ? data.shift_rate_night : data.facility_rate_night,
        }),
        cell: (props) => {
          const rates = props.getValue();
          const hasRate = (rate) =>
            rate !== null && rate !== undefined && rate !== 0 && rate !== '0';
          return (
            <div className="small">
              {hasRate(rates.day_rate) && `$${rates.day_rate} Day`}
              {hasRate(rates.day_rate) && hasRate(rates.night_rate) && ' / '}
              {hasRate(rates.night_rate) && `$${rates.night_rate} Night`}
            </div>
          );
        },
        size: 8,
        minSize: 0,
      },
      {
        header: 'Status',
        accessorFn: (data) => ({
          combined_status: data.combined_status,
          facility_rejection_reason: data.facility_rejection_reason,
          facility_rejection_details: data.facility_rejection_details,
          provider_rejection_reason: data.provider_rejection_reason,
          provider_rejection_details: data.provider_rejection_details,
        }),
        cell: (props) => {
          const facility_relationship = props.getValue();
          return (
            <>
              <ConnectionCombinedStatusBadge status={facility_relationship.combined_status} />
              {facility_relationship.facility_rejection_reason &&
                (facility_relationship.facility_rejection_reason ===
                  FacilityRejectionReasonEnum.OTHER &&
                facility_relationship.facility_rejection_details ? (
                  <div className="text-danger small mt-1">
                    {facility_relationship.facility_rejection_details}
                  </div>
                ) : (
                  <div className="text-danger small mt-1">
                    {
                      window.FACILITY_REJECTION_REASONS[
                        facility_relationship.facility_rejection_reason
                      ]
                    }
                  </div>
                ))}
              {facility_relationship.provider_rejection_reason &&
                (facility_relationship.provider_rejection_reason ===
                  ProviderRejectionReasonEnum.OTHER &&
                facility_relationship.provider_rejection_details ? (
                  <div className="text-danger small mt-1">
                    {facility_relationship.provider_rejection_details}
                  </div>
                ) : (
                  <div className="text-danger small mt-1">
                    {window.REJECTION_REASONS[facility_relationship.provider_rejection_reason]}
                  </div>
                ))}
            </>
          );
        },
        size: 20,
        minSize: 0,
      },
      {
        header: ' ',
        accessorFn: (row: ConnectedFacility) => ({
          facility_status: row.facility_status,
          id: row.id,
          combined_status: row.combined_status,
          fullData: row,
        }),
        cell: (props) => {
          const facility_relationship = props.getValue();

          return (
            <div className="d-flex gap-2 align-items-center">
              <Button
                variant="outline-primary"
                className="btn btn-drop-shadow-sm px-3"
                size="sm"
                onClick={() => setSelectedFacility(facility_relationship.fullData)}
              >
                Open
              </Button>
              <PresentationStatusControl
                id={facility_relationship.id}
                combined_status={facility_relationship.combined_status}
              />
            </div>
          );
        },
        size: 25,
        minSize: 0,
      },
    ],
    []
  );

  return list?.length > 0 ? (
    <>
      <Card className="mb-4 p-3">
        <div className="p-2">
          <h4 className="mb-3">Facilities</h4>
          <PresentationStatusContext.Provider
            value={{
              onchange: onPresentationStatusChange,
              isDisabled: isDisabled,
            }}
          >
            {isGrid && <Table columns={columns} data={list} isLoading={isLoading} />}
            {!isGrid &&
              list.map((item: ConnectedFacility, index) => (
                <FacilityCard data={item} key={index} />
              ))}
          </PresentationStatusContext.Provider>
        </div>
        {(data as unknown as [])?.length > effectiveListSize && (
          <Button
            variant="link"
            className="mx-auto my-1"
            onClick={() => setShowFullList(!showFullList)}
          >
            {showFullList ? 'Show Less' : 'Show More'}
          </Button>
        )}
      </Card>
      <DenyPresentationDialog
        show={!!denialId}
        closeDialog={(result) => handleDenyPresentation(result)}
      />
      <ConfirmationModal
        show={!!approvalId}
        title="Approve Presentation"
        confirmText="Approve"
        text="Do you wish to approve the provider presentation and move forward with credentialing?"
        onHide={async (accepted: boolean) => {
          if (accepted) {
            await handleApprovePresentation();
          }
          setApprovalId(null);
        }}
      />
      {selectedFacility && (
        <ReviewConnectionDialog
          connectionRequest={selectedFacility}
          show={true}
          onSubmit={() => {}}
          closeDialog={handleReviewDialogClose}
          onAccept={() => {}}
          onReject={() => {}}
          onCancel={() => {}}
          openedFromProfile={true}
        />
      )}
    </>
  ) : null;
};
